import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import { createPaymentIntent, confirmPayment, formatAmountForStripe, finalizeSimOrder, getIntent, isValidOptionalPhone } from '../services/stripeService';
import { useNavigate } from 'react-router-dom';
import { secureStorage } from '../Utils/encryption';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true
};

const PaymentForm = ({ totalAmount, cartItems, onPaymentSuccess, onPaymentError, selectedCurrency, validateCartItems, affiliateTid }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [esimDetails, setEsimDetails] = useState(null);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      border: 'none',
      borderRadius: '10px',
      padding: '0',
      maxWidth: '500px',
      width: '90%',
      backgroundColor: 'transparent'
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000
    }
  };

    const handleInstallEsim = () => {
    navigate('/activateEsim'); 
  };

  const handleGoHome = () => {
    navigate('/'); 
  };
 
  const isFormValid = () => {
    return (
      stripe &&
      cardComplete &&
      billingDetails.name.trim() !== '' &&
      billingDetails.email.trim() !== '' &&
      billingDetails.email.includes('@') &&
      isValidOptionalPhone(billingDetails.phone) &&
      termsAccepted &&
      !isProcessing
    );
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
    setEsimDetails(null);
    
    window.location.href = '/';
  };

  const closeFailedModal = () => {
    setShowFailedModal(false);
    setErrorMessage('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate cart items before proceeding with payment
    if (validateCartItems && !validateCartItems()) {
      return;
    }

    if (!stripe || !elements) {
      toast.error(t('paymentForm.errors.stripeNotLoaded'));
      return;
    }

    if (!cardComplete) {
      toast.error(t('paymentForm.errors.cardIncomplete'));
      return;
    }

    if (!billingDetails.name.trim()) {
      toast.error(t('paymentForm.errors.nameRequired'));
      return;
    }

    if (!billingDetails.email.trim() || !billingDetails.email.includes('@')) {
      toast.error(t('paymentForm.errors.emailInvalid'));
      return;
    }

    if (!isValidOptionalPhone(billingDetails.phone)) {
      toast.error(t('paymentForm.errors.phoneInvalid'));
      return;
    }

    if (!termsAccepted) {
      toast.error(t('paymentForm.errors.termsRequired'));
      return;
    }

    // ── FirstPromoter: register this email as a lead (reads affiliate cookie automatically)
    try {
      if (window.fpr) {
        window.fpr('track', { type: 'signup', email: billingDetails.email });
      }
    } catch (e) { /* never block payment if FP fails */ }

    setIsProcessing(true);

    try {
      const phone = billingDetails.phone.trim();
      const orderData = {
        name: billingDetails.name,
        email: billingDetails.email,
        ...(phone ? { phone } : {}),
        items: cartItems.map(item => {
          const baseItem = {
            type: item.plan_type === 'Data Bundles' ? 'data_bundle' : item.plan_type === 'Global eSIM' ? 'global_esim' : 'unlimited',
            country: item.country_code || item.iso3 || 'USA',
            days: item.days || 30,
            no_of_esims: item.esim_count || 1,
            data_usage_allowance: item.gb || 20,
            payment_amount: parseFloat(item.api_calculated_price || item.price) || 0 
          };

        
          if (item.plan_type === 'Unlimited Plans' && item.startDate) {
           
            const startDateTimestamp = new Date(item.startDate).getTime().toString();
            baseItem.start_date = startDateTimestamp;
            console.log('Added start_date for unlimited plan:', {
              originalStartDate: item.startDate,
              convertedTimestamp: startDateTimestamp
            });
          }

          return baseItem;
        })
      };

      
      const intentData = await getIntent({
        name: billingDetails.name,
        email: billingDetails.email,
        ...(phone ? { phone } : {}),
        amount: totalAmount,
        items: orderData.items,
        currency: selectedCurrency
      });
      

      if (intentData.status === false) {
        throw new Error(intentData.message || 'Payment intent creation failed');
      }
      
      if (!intentData.clientSecret) {
        throw new Error('Payment intent not created. Please try again.');
      }
      const cardElement = elements.getElement('card');
      if (!cardElement) {
        throw new Error('Card element not found');
      }
      const { error, paymentIntent } = await stripe.confirmCardPayment(intentData.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: orderData.email,
            ...(phone ? { phone } : {}),
          },
        },
      });
      if (error) {
      
        console.log('Stripe error object:', error);
        console.log('Error code:', error.code);
        console.log('Error decline_code:', error.decline_code);
        console.log('Error message:', error.message);
       
        error.message = error.message || 'Payment failed. Please try again.';
        
        throw error;
      }
      if (paymentIntent.status === 'succeeded') {
        try {
       
          const finalizePayload = {
            ...orderData,
            payment_intent_id: paymentIntent.id,
            currency: selectedCurrency,
            ...(affiliateTid ? { tid: affiliateTid } : {})
          };
          
          console.log('Sending finalize-sim payload:', finalizePayload);
          
          const finalizeResponse = await finalizeSimOrder(finalizePayload);
          
          const successMessage = finalizeResponse.message || t('paymentForm.success.orderPlaced');
          
          setSuccessMessage(successMessage);
          setEsimDetails(finalizeResponse.data);
          const mockPaymentIntent = {
            id: paymentIntent.id,
            status: 'succeeded',
          };
          onPaymentSuccess(mockPaymentIntent);
          secureStorage.removeItem('checkoutSession');
          setShowSuccessModal(true);
        } catch (finalizeError) {
          console.error('Error finalizing order:', finalizeError);
         
          let errorMessage = t('paymentForm.errors.orderProcessingFailed');
          
          if (finalizeError.message) {
            errorMessage = finalizeError.message;
          } else if (finalizeError.response && finalizeError.response.data && finalizeError.response.data.message) {
            errorMessage = finalizeError.response.data.message;
          } else if (finalizeError.response) {
            const status = finalizeError.response.status;
            if (status === 409) {
              errorMessage = t('paymentForm.errors.orderAlreadyProcessed');
            } else if (status === 422) {
              errorMessage = t('paymentForm.errors.orderValidationFailed');
            } else if (status >= 500) {
              errorMessage = t('paymentForm.errors.serverError');
            }
          }
          
          setErrorMessage(errorMessage);
          setShowFailedModal(true);
          onPaymentError(finalizeError);
        }
      } else if (paymentIntent.status === 'requires_action' || paymentIntent.status === 'requires_confirmation') {
       
        const errorMessage = t('paymentForm.errors.authenticationRequired');
        setErrorMessage(errorMessage);
        setShowFailedModal(true);
        onPaymentError(new Error(errorMessage));
      } else {
       
        const errorMessage = t('paymentForm.errors.paymentStatus', { status: paymentIntent.status });
        setErrorMessage(errorMessage);
        setShowFailedModal(true);
        onPaymentError(new Error(errorMessage));
      }
    } catch (error) {
      console.error('Order error:', error);
      console.log('Full error object:', error);
      console.log('Error code:', error.code);
      console.log('Error decline_code:', error.decline_code);
      console.log('Error message:', error.message);
      console.log('Error type:', typeof error);
      console.log('Error constructor:', error.constructor.name);
      
     
      let message = t('paymentForm.errors.paymentFailed');
      
      if (error && typeof error === 'object') {
        if (error.message) {
          message = error.message;
        } else if (error.response && error.response.data && error.response.data.message) {
          message = error.response.data.message;
        }
      }
      
      setErrorMessage(message);
      setShowFailedModal(true);
      onPaymentError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="payment-form">
        <div className="choose-payment">
          <h3>{t('paymentForm.payWithCard')}</h3>
          <div className="card-element-container">
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={handleCardChange}
            />
          </div>
        </div>

        <div className="choose-payment">
          <h3>{t('paymentForm.billingInfo')}</h3>
          <div className="form-group">
            <label>{t('paymentForm.name')} *</label>
            <input
              type="text"
              name="name"
              className="form-control"
              placeholder={t('paymentForm.namePlaceholder')}
              value={billingDetails.name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('paymentForm.email')} *</label>
            <input
              type="email"
              name="email"
              className="form-control"
              placeholder={t('paymentForm.emailPlaceholder')}
              value={billingDetails.email}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('paymentForm.phone')}</label>
            <input
              type="tel"
              name="phone"
              autoComplete="tel"
              className="form-control"
              placeholder={t('paymentForm.phonePlaceholder')}
              value={billingDetails.phone}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <label className="save-check mb-3">
          {t('paymentForm.termsText')} <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">{t('paymentForm.termsAndConditions')}</Link>, <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">{t('paymentForm.privacyPolicy')}</Link> {t('paymentForm.and')} <Link to="/refund-policy" target="_blank" rel="noopener noreferrer">{t('footer.legal.refundPolicy')}</Link>.
          <input 
            type="checkbox" 
            checked={termsAccepted}
            onChange={handleTermsChange}
          />
          <span className="checkmark"></span>
        </label>

        <button
          type="submit"
          className="view-all get-sim"
          disabled={!isFormValid()}
          style={{
            opacity: isFormValid() ? 1 : 0.6,
            cursor: isFormValid() ? 'pointer' : 'not-allowed'
          }}
        >
          {isProcessing ? t('paymentForm.processing') : t('paymentForm.placeOrder', { amount: totalAmount.toFixed(2) })}
        </button>
      </form>

      {/* Payment Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onRequestClose={closeSuccessModal}
        style={customStyles}
        contentLabel="Payment Success"
      >
        <div className="paymentsuccess">
          <div className="modal-content">
            <div className="modal-header">
          
              <button type="button" onClick={closeSuccessModal} className="close" data-dismiss="modal" aria-label="Close">
                <img src={window.getPublicUrl('images/close.svg')} alt="Close" />
              </button>
            </div>
            <div className="modal-body">
                  <div className="success-img d-flex justify-content-center">
                <img src={window.getPublicUrl('images/check-circle.svg')} className="img-fluid" alt="Success icon" />
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <h3>{t('paymentForm.success.title')}</h3>
                  <p className='mb-0'>{successMessage || t('paymentForm.success.message')}</p>
                  {/* {esimDetails && esimDetails.results && esimDetails.results.length > 0 && (
                    <div className="mt-3">
                      <p className="mb-1"><strong>Order Summary:</strong></p>
                      <p className="mb-1">• Requested: {esimDetails.requested_count} eSIM(s)</p>
                      <p className="mb-1">• Successful: {esimDetails.successful_count} eSIM(s)</p>
                      {esimDetails.results[0].sim && (
                        <p className="mb-1">• SIM Number: {esimDetails.results[0].sim}</p>
                      )}
                    </div>
                  )} */}
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button
                type="button"
                className="btn view-all get-sim"
                onClick={handleInstallEsim}
              >
                {t('paymentForm.success.howToInstall')}
              </button>

              <button
                type="button"
                className="view-all get-sim Continue"
                onClick={handleGoHome}
              >
                {t('paymentForm.success.goBackHome')}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Payment Failed Modal */}
      <Modal
        isOpen={showFailedModal}
        onRequestClose={closeFailedModal}
        style={customStyles}
        contentLabel="Payment Failed"
      >
        <div className="paymentsuccess">
          <div className="modal-content">
            <div className="modal-header">
              {/* <div className="success-img">
                <img src={window.getPublicUrl('images/Cross.svg')} className="img-fluid" alt="Error icon" />
              </div> */}
              <button type="button" onClick={closeFailedModal} className="close" data-dismiss="modal" aria-label="Close">
                <img src={window.getPublicUrl('images/close.svg')} alt="Close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-12">
                  <h3>{t('paymentForm.failed.title')}</h3>
                  <p className='mb-0'>{errorMessage || t('paymentForm.failed.message')}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button
                type="button"
                className="btn view-all get-sim"
                onClick={closeFailedModal}
              >
                {t('paymentForm.failed.tryAgain')}
              </button>

              <button
                type="button"
                className="view-all get-sim Continue"
                onClick={handleGoHome}
              >
                {t('paymentForm.failed.goBackHome')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PaymentForm; 